import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import CanonicalLink from 'marketplace-ui-common/components/elements/CanonicalLink';
import MetaDescription from 'marketplace-ui-common/components/elements/MetaDescription';
import MetaOpenGraph from 'marketplace-ui-common/components/elements/MetaOpenGraph';
import MetaTwitter from 'marketplace-ui-common/components/elements/MetaTwitter';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import { useScreenSize } from 'marketplace-ui-common/hooks/useScreenSize';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import HSPageViewTracker from 'marketplace-ui-common/tracking/components/HSPageViewTracker';
import { Fragment, useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useLocation } from 'react-router-dom';
import HomepageAccreditationCardGrid from '../components/homepage/HomepageAccreditationCardGrid';
import HomepageRecommendations from '../components/homepage/HomepageRecommendations';
import HomepageServicesCarousel from '../components/homepage/HomepageServicesCarousel';
import FindPartnerWizardResultsCarousel from '../components/homepage/questionnaire/FindPartnerWizardResultsCarousel';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import { useSocialScreenshotUrl } from '../hooks/useSocialScreenshot';
import { getServicesRequestState } from '../slices/serviceCatalogSlice';
import { getRecentActivity } from '../utils/homepage';

// @ts-expect-error untyped import
import UsageViewTracker from '../components/tracking/UsageViewTracker';
// @ts-expect-error untyped import
import { PublicAppComponent, getCanonicalAppContext } from '../selectors/app';
// @ts-expect-error untyped import
import { getFullLocationPathname, getFullLocationUrl } from '../utils/urls';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Homepage = () => {
  const lang = getLang();
  const location = useLocation();
  const {
    setDefaultEventProperties
  } = useContext(StorefrontUsageTrackerContext);
  useEffect(() => {
    document.title = unescapedText('ecosystem-marketplace-solutions-ui.storefront.htmlTitle.default');
  }, []);
  const recentActivity = getRecentActivity();
  const screenSize = useScreenSize();
  const isMobile = screenSize === ScreenSizes.XS || screenSize === ScreenSizes.SM;
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const catalogServicesRequestState = useSelector(getServicesRequestState);
  const catalogServicesRequestStateSucceeded = useMemo(() => isSucceeded(catalogServicesRequestState), [catalogServicesRequestState]);
  useEffect(() => {
    setDefaultEventProperties({
      screen: 'storefront home'
    });
  }, [setDefaultEventProperties]);
  const defaultMetaDescription = unescapedText('ecosystem-marketplace-solutions-ui.storefront.metaDescription.default');
  const canonicalUrl = useMemo(() => getFullLocationUrl(location, canonicalAppContext, lang), [canonicalAppContext, lang, location]);
  const socialScreenshotUrl = useSocialScreenshotUrl();
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [catalogServicesRequestStateSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: "HOMEPAGE_LOAD"
    }), /*#__PURE__*/_jsx(PublicAppComponent, {
      children: /*#__PURE__*/_jsx(DocumentHead, {
        children: /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(MetaDescription, {
            content: defaultMetaDescription
          }), /*#__PURE__*/_jsx(CanonicalLink, {
            href: canonicalUrl
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "card"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "site"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "image",
            content: socialScreenshotUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "type"
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "url",
            content: canonicalUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "title",
            content: unescapedText('ecosystem-marketplace-solutions-ui.storefront.htmlTitle.default')
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "description",
            content: defaultMetaDescription
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "image",
            content: socialScreenshotUrl
          }), /*#__PURE__*/_jsx(PrerenderReady, {
            ready: catalogServicesRequestStateSucceeded
          })]
        })
      })
    }), /*#__PURE__*/_jsx(MainSection, {
      "data-test-id": "homepage-container",
      color: "light",
      children: /*#__PURE__*/_jsxs(MainSectionInner, {
        children: [recentActivity && recentActivity.type && /*#__PURE__*/_jsx(View, {
          paddingBlockEnd: "800",
          as: "div",
          children: /*#__PURE__*/_jsx(FindPartnerWizardResultsCarousel, {
            isMobile: isMobile
          })
        }), catalogServicesRequestStateSucceeded && /*#__PURE__*/_jsx(HomepageServicesCarousel, {
          isMobile: isMobile
        }), /*#__PURE__*/_jsx(HomepageAccreditationCardGrid, {})]
      })
    }), /*#__PURE__*/_jsx(MainSection, {
      children: /*#__PURE__*/_jsx(MainSectionInner, {
        children: /*#__PURE__*/_jsx(HomepageRecommendations, {})
      })
    }), /*#__PURE__*/_jsx(UsageViewTracker, {
      active: catalogServicesRequestStateSucceeded,
      name: "storefront-pageview",
      properties: {
        screen: 'storefront home'
      }
    }), /*#__PURE__*/_jsx(HSPageViewTracker, {
      pathname: getFullLocationPathname(location.pathname, canonicalAppContext, lang)
    })]
  });
};
export default Homepage;